import { VideoProject } from "@/services/bedrock";

import { getKanbanColumnId, KanbanColumnId } from "./kanbanColumns";

const EDITING_COLUMN_IDS = new Set<KanbanColumnId>(["submitted_for_editing", "editing"]);
const TESTING_COLUMN_IDS = new Set<KanbanColumnId>(["submitted_for_testing", "testing"]);

function completionTimestamp(project: VideoProject): number {
  const raw = project.qaDate || project.editedDate || project.updatedDate;
  const timestamp = raw ? Date.parse(raw) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function hasQueueRole(project: VideoProject, userId: number, role: VideoProject.QueueAssignment["role"]): boolean {
  return (
    project.queueAssignments?.some(assignment => assignment.staffUserId === userId && assignment.role === role) ?? false
  );
}

function isFocusedEditor(project: VideoProject, userId: number): boolean {
  return project.editorUserId === userId || hasQueueRole(project, userId, "EDITOR");
}

function isFocusedQa(project: VideoProject, userId: number): boolean {
  return project.qaUserId === userId || hasQueueRole(project, userId, "QA");
}

function kanbanQueuePriorityRank(project: VideoProject, columnId: KanbanColumnId, currentUserId: number): number {
  if (columnId === "completed") return 0;

  const assignmentsLoaded = project.queueAssignments !== undefined;
  const hasEditor = isFocusedEditor(project, currentUserId);
  const hasQa = isFocusedQa(project, currentUserId);

  if (EDITING_COLUMN_IDS.has(columnId)) {
    if (hasEditor) return 0;
    if (hasQa) return 1;
    if (assignmentsLoaded) return 2;
    return project.isPriority ? 0 : 2;
  }

  if (TESTING_COLUMN_IDS.has(columnId)) {
    if (hasQa) return 0;
    if (hasEditor) return 1;
    if (assignmentsLoaded) return 2;
    return project.isPriority ? 0 : 2;
  }

  return 2;
}

function compareKanbanQueueOrder(
  a: VideoProject,
  b: VideoProject,
  columnId: KanbanColumnId,
  currentUserId: number
): number {
  if (columnId === "completed") {
    const timeDiff = completionTimestamp(b) - completionTimestamp(a);
    if (timeDiff !== 0) return timeDiff;
    return b.id - a.id;
  }

  const rankDiff =
    kanbanQueuePriorityRank(a, columnId, currentUserId) - kanbanQueuePriorityRank(b, columnId, currentUserId);
  if (rankDiff !== 0) return rankDiff;
  return a.id - b.id;
}

export function insertProjectInKanbanQueueOrder(
  projects: VideoProject[],
  project: VideoProject,
  columnId: KanbanColumnId,
  currentUserId: number
): VideoProject[] {
  const others = projects.filter(item => item.id !== project.id);
  const index = others.findIndex(item => compareKanbanQueueOrder(project, item, columnId, currentUserId) < 0);
  if (index === -1) return [...others, project];
  return [...others.slice(0, index), project, ...others.slice(index)];
}

export function isPriorityFor(project: VideoProject, currentUserId: number): boolean {
  if (project.editorUserId === currentUserId || project.qaUserId === currentUserId) {
    return true;
  }

  const requiredRole = project.status.endsWith("EDITING") ? "EDITOR" : project.status.endsWith("TESTING") ? "QA" : null;
  if (!requiredRole) return false;

  return hasQueueRole(project, currentUserId, requiredRole);
}

export function buildOptimisticKanbanProjectPatch(
  project: VideoProject,
  args: {
    status: VideoProject.Status;
    event: VideoProject.Event;
    currentUserId: number;
    assignedUserId?: number;
  }
): Partial<VideoProject> {
  const now = new Date().toISOString();
  const assigneeId = args.assignedUserId ?? args.currentUserId;
  const patch: Partial<VideoProject> = {
    status: args.status,
    statusLabel: VideoProject.StatusMeta[args.status]?.label,
    statusDate: now,
    updatedDate: now,
  };

  switch (args.event) {
    case VideoProject.Event.START_EDITING:
      patch.editorUserId = project.editorUserId ?? assigneeId;
      break;
    case VideoProject.Event.START_TESTING:
      patch.qaUserId = project.qaUserId ?? assigneeId;
      break;
    case VideoProject.Event.COMPLETE:
      patch.editedDate = now;
      break;
    case VideoProject.Event.APPROVE_QA:
      patch.qaDate = now;
      patch.qaStatus = "APPROVED";
      break;
    case VideoProject.Event.REJECT_QA:
      patch.qaDate = now;
      patch.qaStatus = "REJECTED";
      patch.qaUserId = args.currentUserId;
      break;
    case VideoProject.Event.REQUEST_REVISION:
      patch.revisionCount = (project.revisionCount ?? 0) + 1;
      break;
    default:
      break;
  }

  const nextProject = { ...project, ...patch };
  patch.isPriority = isPriorityFor(nextProject, args.currentUserId);
  return patch;
}
